import { useMemo } from "react"
import * as palette from "../symbols/palette"
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material"
import _ from "lodash"

const ArchiMateElementSelect = ({
  type,
  setType,
  label = "Element Type",
  filterTypes = [],
  disabled = false,
  error,
  includeNoneOption = false,
}) => {
  const elementOptions = useMemo(() => {
    const filterType =
      filterTypes.length > 0
        ? (item) => filterTypes.includes(item.index)
        : () => true

    const result = palette.ELEMENT_INDEX.filter((item) => filterType(item)).map(
      (item) => {
        return {
          id: Number(item.index),
          label: palette.formatLabel(item.name),
          layer: item.layer.name,
        }
      }
    )

    if (includeNoneOption) {
      result.unshift({
        id: -1,
        label: "None",
        layer: "None",
      })
    }
    return result
  }, [filterTypes, includeNoneOption])

  const elementsForSelect = useMemo(() => {
    const grouped = _.groupBy(elementOptions, "layer")

    const groups = Object.keys(grouped).map((label) => {
      return {
        label: label,
        color:
          palette.LAYERS.find((x) => x.name === label)?.color || "transparent",
        options: grouped[label],
      }
    })

    const result = groups.flatMap((group) => {
      return [
        {
          label: group.label,
          layer: group.label,
          color: group.color,
          group: true,
        },
        ...group.options,
      ]
    })

    return result
  }, [elementOptions])

  const elementTypeMenuItems = useMemo(() => {
    const menuItems = elementsForSelect.map((option) =>
      option.group ? (
        <MenuItem
          key={option.label}
          sx={{ backgroundColor: option.color }}
          value=""
        >
          {option.label}
        </MenuItem>
      ) : (
        <MenuItem key={option.label} value={option.id}>
          {option.label}
        </MenuItem>
      )
    )

    return menuItems
  }, [elementsForSelect])

  const disabledStyles = {
    "& .MuiSelect-select": { color: "rgba(0, 0, 0, 0.38)" },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0, 0, 0, 0.38)",
    },
  }

  const disabledLabel = {
    color: "rgba(0, 0, 0, 0.38)", // Match disabled color
    "&.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.38)", // Explicitly style disabled state
    },
  }

  return (
    <FormControl>
      <InputLabel id="archimate-select" sx={disabled ? disabledLabel : {}}>
        {label}
      </InputLabel>
      <Select
        labelId="archimate-select"
        id="archimate-select-id"
        error={Boolean(error)}
        label={label}
        onChange={(e) => {
          setType(e.target.value)
        }}
        variant="outlined"
        size="small"
        sx={{
          width: "250px",
          ...(disabled ? disabledStyles : {}),
        }}
        value={type}
        disabled={disabled}
      >
        {elementTypeMenuItems}
      </Select>
      {error && (
        <FormHelperText sx={{ color: "error.main" }}>{error}</FormHelperText>
      )}
    </FormControl>
  )
}

export default ArchiMateElementSelect
